// @givmo/org-console — the gear's ONE composed settings page (D-CPO-33; approved by D-CPO-34; the
// settings framework, Org Machinery OM-17 design 2 + 4, is the paradigm this composes).
//
// ══ ONE PAGE, FOUR SECTIONS, COMPOSED FOR THE PERSON ═══════════════════════════════════════════
//
// "THINK LIKE WHAT WOULD A USER EXPECT!" — a person pressing the gear (or the sidebar's LAST row)
// never chooses between a personal settings menu and an org settings menu. The page is composed
// for them, in this order on EVERY door:
//
//   1. **"<Module> settings"** — the org-scoped settings of the module the page was opened FROM
//      (its origin): the definitions the server SERVES for it (seam A, each gated on the ONE
//      permission string its write requires), then the panels the module DECLARES (seam B,
//      likewise). ONLY the current module's; from an Admin Console page the module IS the
//      organization, so there is no module section. (The shell's own per-module card, "Who can
//      approve requests to open <Module>", was dropped on every app of both consoles: EP-WEB36,
//      D-EP-58 — a module's Owners and the Global Admins always approve.)
//   2. **"Your settings"** — the person's own preferences, served by the server and rendered by
//      `OrgAdminPreferencesPanel` exactly as before, after any card the host leads with for the
//      person's own account (`yourSettingsLead`: a console's "Sign-in & security", AS-WEB11), and
//      the *Leave organization* card LAST where the host wires `onLeft` and the standing read carries
//      `leave_org` (EP-MNT9 (c)). Never absent: the landing key is served to every admin.
//   3. **"Organization details"** — the FACTS (Org Machinery OM-64, Design 4C §1; the frames approved
//      by D-CPO-69): first a card per outside record the server serves (`records`: the IRS's, for some
//      classes), then the kept facts by `order`, each in the one card shape, editable iff its served
//      `can_change`. Which facts and records a person is served is the server's read rule (§4,
//      D-CPO-68: a fact shows only while an app that uses it is on, or Givmo uses it), so facts are
//      never re-filtered on `requires` here; they wait for a settled standing all the same. A fact sits
//      HERE on every door, never in a module's section (D-CPO-63). No host card, and no line under the
//      heading (D-CPO-69 question 3). Nothing served: no section.
//   4. **"Organization settings"** — every org-level SETTING the person holds a string or capability
//      for, at the FOOT on every door: knobs first — the organization's own definitions the server
//      SERVES that are not facts (seam A, `module: "org"`, design 4A §8 item 3), then the host's
//      declared knob cards — then the host's GRAVE acts, each by `order`, and the *Deactivate
//      organization* card ALWAYS last (its own three-way gate untouched; its line says what the act
//      deletes, read off the facts this page shows, so it waits for the settings read).
//
// ══ THE RULES ══════════════════════════════════════════════════════════════════════════════════
//
//   * Empty-section rule: a section renders iff it has ≥1 served or held item; never a placeholder;
//     the page never renders empty (section 2 always has the landing key). The module section is
//     SHOWN only once something in it draws: held panels that all render nothing (a dark backend
//     half) keep it mounted, their reads running, but hidden, heading included (CBG-UX14).
//   * Gates are STRINGS and CAPABILITIES off the standing read — never a tier, never an admin flag
//     (the build law; `build-law.test.ts` lists this file). A not-reported standing
//     (`permissions === null`) renders section 2 ONLY — an org setting is never drawn on a guess.
//   * The module section needs the module ON (a not-reported standing is permissive, the tile's
//     posture) and CURRENT (the origin); another module's served definitions never render here.
//   * ONE settings read serves sections 1, 3 and 4, and it runs from EVERY door once the standing is
//     settled — the Admin Console's included, where there is no module (design 4A §8 item 2).
//   * The seams are DATA; this file holds no setting, no module and no class. A module's panel
//     arrives as a declaration with its own component; a class's cards and copy arrive through the
//     host's declarations and vocab.
//
// FILE HOME: the OM-17 Mechanics proposed `org-settings-page.tsx`; the composer was already here
// (the OM-9 build) and its consumers, tests and rails name this file, so it grew in place.
//
// DATA CONTRACT: the `org-settings-*` test-id family (fixed, not prefixed by the mount):
// `org-settings-module` (+ `-def-<key>`, `-<panel id>`) · `-your` · `-details` (+ `-def-<key>`,
// `-record-<source>`) · `-organization` (+ `-def-<key>`, `-<card id>`).

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { testIdProp } from '@givmo/design-system';
import type { ModuleStandings } from './module-verdict';
import { OrgAdminPreferencesPanel, type LandingOption } from './org-admin-preferences-panel';
import type { OrgConsoleConfig } from './org-console-config';
import { decodeOrgAddress, orgMemberName } from './org-decode';
import { OrgDeactivationCard } from './org-deactivation';
import { OrgLeaveCard } from './org-leave-organization';
import type { OrgAddressSameAs } from './settings-address-card';
import { OrgSettingDefinitionCard } from './settings-definition-card';
import { OrgSettingRecordCard } from './settings-record-card';
import {
  ORG_SETTING_GROUP,
  holdsRequirement,
  type ModuleSettingsPanel,
  type OrganizationSettingsCard,
} from './settings-declarations';
import { CAPABILITY_DEACTIVATE_ORG, CAPABILITY_LEAVE_ORG, holdsCapability } from './standing-read';
import { useOrgMembersExhausted, useOrgSettings } from './use-org-console';
import type { OrgSettingDefinition } from './wire-types';

export const ORG_SETTINGS_TEST_ID = 'org-settings';
const PREFIX = ORG_SETTINGS_TEST_ID;

/** The one line that separates the person's section from the organization's. */
export const YOUR_SETTINGS_HINT = 'Nothing here changes what anyone else sees.';

/** The facts' section and the choices' section — Jonathan's headings (2026-10-18 15:19 PDT). */
export const ORGANIZATION_DETAILS_TITLE = 'Organization details';
export const ORGANIZATION_SETTINGS_TITLE = 'Organization settings';

/**
 * The same line where the section ends in *Leave organization* (EP-MNT9 (c)): leaving changes the
 * team everyone else sees, so the line says what stays true — nobody else's access moves.
 */
export const YOUR_SETTINGS_HINT_WITH_LEAVE = 'Nothing here changes anyone else’s access.';

/**
 * The Settings page's line for a single choice that did not save. A module's own cards say these
 * words (Compliance's repeat them, and its host's test holds the two equal). The name is
 * historical: it was first the line of the per-module approval card, dropped at EP-WEB36.
 */
export const APPROVAL_SAVE_FAILED_LINE =
  'We couldn’t save that. Nothing was changed — please try again.';

/** The module the page was opened FROM, resolved by the host: its slug, its label, its declared panels. */
export interface OrgSettingsModule {
  slug: string;
  /** The host's word for the module — "<label> settings" is the section's heading. */
  label: string;
  /** The module's seam-B declaration (`ModuleSettings.panels`); empty when it declares none. */
  panels: readonly ModuleSettingsPanel[];
}

export interface OrgSettingsPanelProps {
  config: OrgConsoleConfig;
  /** The apps this person can OPEN, for the Landing card — the host's derivation. */
  landingOptions: readonly LandingOption[];
  /** What "Default" resolves to right now, as a label. Absent ⇒ the hint line is omitted. */
  defaultResolvesTo?: string | null;
  /**
   * The module the page was opened from, or `null` from an Admin Console page, an Organization
   * page, or an unknown origin (a direct load with nothing to frame it).
   */
  module?: OrgSettingsModule | null;
  /** The caller's per-module standing off the ONE standing read, or `null` when not reported. */
  standings?: ModuleStandings | null;
  /** The caller's EFFECTIVE permission set off the ONE standing read, or `null` when not reported. */
  permissions: readonly string[] | null;
  /** The caller's capabilities off the ONE standing read, or `null` when not reported. */
  capabilities: readonly string[] | null;
  /** The host's Organization-section cards (design 3) — class data. */
  organizationCards?: readonly OrganizationSettingsCard[];
  /**
   * The host's own card(s) for the PERSON, drawn first under "Your settings" — the console's
   * account-wide "Sign-in & security" card (AS-WEB11, D-AS-21's A4 placement). The host decides
   * whether there is one: a console with no passkey origin passes nothing.
   */
  yourSettingsLead?: ReactNode;
  /** Follow an in-app destination with the host's router instead of a document load. */
  onNavigate?: (href: string) => void;
  /** Whether ANY mounted panel holds unsaved changes — the host's unsaved-changes guard listens. */
  onDirtyChange?: (dirty: boolean) => void;
  /** The host's sign-out and door hop after the deactivation act lands. */
  onDeactivated: () => void;
  /**
   * The host's sign-out and door hop once the person has taken themselves off the team. Absent ⇒ the
   * *Leave organization* card never renders, whatever the standing read says: a person who has left
   * must not be left standing on a console that can no longer read their organization.
   */
  onLeft?: () => void;
}

export interface OrgSettingsSectionProps {
  /** The section's id — its anchor and the root of its testid. */
  id: string;
  title: string;
  /** One line under the heading. Omit rather than pad. */
  hint?: string;
  testId: string;
  /**
   * Mounted but not shown, heading included: the module section while every one of its panels
   * renders nothing (see `PanelSlot`). The `hidden` attribute, which the stylesheet's base layer keeps
   * hidden over the section's own `flex`.
   */
  hidden?: boolean;
  children: ReactNode;
}

/** One section of the page: an `h2`, an optional one-line hint, and the cards. */
export function OrgSettingsSection({
  id,
  title,
  hint,
  testId,
  hidden = false,
  children,
}: OrgSettingsSectionProps) {
  const headingId = `${testId}-heading`;
  return (
    <section
      id={id}
      aria-labelledby={headingId}
      hidden={hidden || undefined}
      className="flex flex-col gap-16"
      {...testIdProp(testId)}
    >
      <div className="flex flex-col gap-4">
        <h2 id={headingId} className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
          {title}
        </h2>
        {hint !== undefined && (
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            {hint}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

/**
 * Whether the module is ON for this organization. A not-reported standing — no map, or no entry
 * for this module — is permissive (the tile rule's posture): the page was opened from the module,
 * and every item in the section gates itself on a string besides.
 */
function moduleIsOn(standings: ModuleStandings | null, slug: string): boolean {
  // A not-reported MAP is permissive; a slug MISSING from a REPORTED map is `absent` — `verdictFor`'s
  // rule (key-presence is carried for exactly this), so no module section is drawn for it.
  if (standings === null) return true;
  const standing = standings[slug];
  if (standing === undefined) return false;
  if (standing.activationState !== undefined) return standing.activationState === 'on';
  return standing.orgEnabled;
}

/** Whether a host card's gate is held — its class string, or its capability. */
function cardIsHeld(
  card: OrganizationSettingsCard,
  permissions: readonly string[],
  capabilities: readonly string[] | null,
): boolean {
  if (card.requires !== undefined) return holdsRequirement(permissions, card.requires);
  return holdsCapability(capabilities, card.capability);
}

function byOrder<T extends { order: number }>(a: T, b: T): number {
  return a.order - b.order;
}

/**
 * A declared panel's slot: on unmount it reports the panel CLEAN, so the guard never stays armed
 * against nothing. A panel that renders NOTHING (one whose backend half is dark: it holds its string,
 * but its read answers the route miss — Board's calendar, CBG-CAL1) leaves the slot empty, and an
 * empty slot takes no space: `empty:hidden`, so the section's gap never doubles around it.
 *
 * It also reports whether the panel renders anything (`onFilled`, read off the slot's own children
 * as they change), because a section whose every panel renders nothing must not stand as a bare
 * heading (CBG-UX14: Board's one panel, the calendar, renders nothing while the calendar is dark).
 * Before paint, so a panel that renders at once never shows its section hidden first.
 */
function PanelSlot({
  testId,
  registerDirty,
  onFilled,
  children,
}: {
  testId: string;
  registerDirty: (dirty: boolean) => void;
  onFilled: (filled: boolean) => void;
  children: ReactNode;
}) {
  const slot = useRef<HTMLDivElement>(null);
  useEffect(() => () => registerDirty(false), [registerDirty]);
  useLayoutEffect(() => {
    const element = slot.current;
    if (element === null) return undefined;
    const report = () => onFilled(element.childNodes.length > 0);
    report();
    const observer = new MutationObserver(report);
    observer.observe(element, { childList: true });
    return () => {
      observer.disconnect();
      onFilled(false);
    };
  }, [onFilled]);
  return (
    <div ref={slot} className="empty:hidden" {...testIdProp(testId)}>
      {children}
    </div>
  );
}

export function OrgSettingsPanel({
  config,
  landingOptions,
  defaultResolvesTo,
  module = null,
  standings = null,
  permissions,
  capabilities,
  organizationCards = [],
  yourSettingsLead,
  onNavigate,
  onDirtyChange,
  onDeactivated,
  onLeft,
}: OrgSettingsPanelProps) {
  const orgRef = config.orgRef;
  // A not-reported standing renders section 2 only: nothing below reads `permissions` as `null`.
  const settled = permissions;
  const currentModule = module !== null && settled !== null && moduleIsOn(standings, module.slug);
  // ONE read for sections 1 and 3, from every door once the standing is settled (§8 item 2).
  const served = useOrgSettings(orgRef, settled !== null);

  // ── Section 1: the current module's settings ─────────────────────────────────────────────────
  const definitions = useMemo(() => {
    if (!currentModule || module === null || settled === null) return [];
    return (served.data?.definitions ?? [])
      .filter(
        (definition) =>
          definition.module === module.slug && holdsRequirement(settled, definition.requires),
      )
      .sort(byOrder);
  }, [currentModule, module, served.data, settled]);
  const panels = useMemo(() => {
    if (!currentModule || module === null || settled === null) return [];
    return module.panels.filter((panel) => holdsRequirement(settled, panel.requires)).sort(byOrder);
  }, [currentModule, module, settled]);
  const showsModule = definitions.length > 0 || panels.length > 0;
  // Which held panels render something (each slot reports it). The section is MOUNTED for every held
  // panel, so the panels' reads run, but SHOWN only once a definition or a panel draws: a module whose
  // one panel's backend half is dark (Board's calendar, CBG-UX14) leaves no bare heading behind.
  const [filledPanels, setFilledPanels] = useState<ReadonlySet<string>>(() => new Set());
  const reportFilledFor = useMemo(() => {
    const cache = new Map<string, (filled: boolean) => void>();
    return (id: string) => {
      let fn = cache.get(id);
      if (fn === undefined) {
        fn = (filled: boolean) =>
          setFilledPanels((prior) => {
            if (prior.has(id) === filled) return prior;
            const next = new Set(prior);
            if (filled) next.add(id);
            else next.delete(id);
            return next;
          });
        cache.set(id, fn);
      }
      return fn;
    };
  }, []);
  const moduleDraws = definitions.length > 0 || panels.some((panel) => filledPanels.has(panel.id));

  // ── The unsaved-changes guard: dirty per panel, reported as one boolean ──────────────────────
  const dirtyRef = useRef(new Map<string, boolean>());
  const reportDirty = useCallback(
    (id: string, dirty: boolean) => {
      const map = dirtyRef.current;
      if ((map.get(id) ?? false) === dirty) return;
      map.set(id, dirty);
      onDirtyChange?.([...map.values()].some(Boolean));
    },
    [onDirtyChange],
  );
  const registerDirtyFor = useMemo(() => {
    const cache = new Map<string, (dirty: boolean) => void>();
    return (id: string) => {
      let fn = cache.get(id);
      if (fn === undefined) {
        fn = (dirty: boolean) => reportDirty(id, dirty);
        cache.set(id, fn);
      }
      return fn;
    };
  }, [reportDirty]);

  // ── Section 3: Organization details — the facts (Design 4C §1) ───────────────────────────────
  // Served is the verdict: the server serves a fact to whoever may change it and, read-only, to the
  // holders of an app that reads it, so no `requires` filter here. Settled first, all the same.
  const factDefinitions = useMemo(() => {
    if (settled === null) return [];
    return (served.data?.definitions ?? [])
      .filter((definition) => definition.module === ORG_SETTING_GROUP && definition.fact)
      .sort(byOrder);
  }, [served.data, settled]);
  const records = useMemo(
    () => (settled === null ? [] : (served.data?.records ?? [])),
    [served.data, settled],
  );
  const showsDetails = records.length > 0 || factDefinitions.length > 0;
  // The outside record's mailing address a kept address may copy (D-CPO-61, OM-59) — data, whichever
  // record serves an address.
  const sameAs = useMemo((): OrgAddressSameAs | null => {
    for (const record of records) {
      for (const fact of record.facts) {
        if (fact.kind === 'address' && fact.value !== null && typeof fact.value === 'object') {
          return { label: fact.label, address: fact.value };
        }
      }
    }
    return null;
  }, [records]);
  // Deactivating clears the physical address the organization entered (D-CPO-62) — the one kept
  // address today (Design 4C §5) — whether or not this page shows it: D-CPO-68 hides it while no
  // app that reads it is on, and keeps its value. So the Deactivate card and its dialog say so
  // wherever one was entered (D-CPO-69 question 8, OM-92), as the server tells someone who may
  // deactivate. A backend that tells nothing leaves the page to read it off the facts it shows.
  const clearsEnteredAddress =
    served.data?.entered_address_on_file ??
    factDefinitions.some(
      (definition) =>
        definition.kind === 'address' &&
        decodeOrgAddress(served.data?.values[definition.key]) !== null,
    );

  // ── Section 4: Organization settings ────────────────────────────────────────────────────────
  // The organization's own served definitions that are not facts — knobs, so they lead (design 2).
  const orgDefinitions = useMemo(() => {
    if (settled === null) return [];
    return (served.data?.definitions ?? [])
      .filter(
        (definition) =>
          definition.module === ORG_SETTING_GROUP &&
          !definition.fact &&
          holdsRequirement(settled, definition.requires),
      )
      .sort(byOrder);
  }, [served.data, settled]);
  const heldCards = useMemo(() => {
    if (settled === null) return [];
    const held = organizationCards.filter((card) => cardIsHeld(card, settled, capabilities));
    return [
      ...held.filter((card) => card.grave !== true).sort(byOrder),
      ...held.filter((card) => card.grave === true).sort(byOrder),
    ];
  }, [capabilities, organizationCards, settled]);
  // The Deactivate card renders only for a holder of the capability AND a host that wrote the copy;
  // the card itself withholds until the organization's name has resolved (the typed confirmation
  // is against it). Its gate is unchanged by the framework.
  const showsDeactivate =
    settled !== null &&
    holdsCapability(capabilities, CAPABILITY_DEACTIVATE_ORG) &&
    config.vocab.deactivation !== undefined &&
    (config.vocab.orgName ?? '').trim().length > 0;
  // What the act deletes is a claim, so the card waits for the read that decides it — a "Nothing is
  // deleted." flashed before an entered address is read would be untrue for that moment. A read that
  // fails or is dark answers too, and then the page shows no address.
  const drawsDeactivate = showsDeactivate && !served.isPending;
  const showsOrganization = orgDefinitions.length > 0 || heldCards.length > 0 || drawsDeactivate;
  // The Leave card: a host that signs the person out after, and a settled positive on the capability.
  const showsLeave =
    onLeft !== undefined && settled !== null && holdsCapability(capabilities, CAPABILITY_LEAVE_ORG);

  // ── Who last changed a setting: the roster names them, read only once a drawn setting has a change ─
  const changed = [...definitions, ...factDefinitions, ...orgDefinitions].some(
    (definition) => served.data?.state[definition.key] !== undefined,
  );
  const roster = useOrgMembersExhausted(orgRef, changed);
  // Absent until the roster has answered (or failed), so a card's line never flashes a stand-in.
  const personName = useMemo(() => {
    if (!roster.isSuccess && !roster.isError) return undefined;
    const members = roster.data ?? [];
    return (userId: string): string | undefined => {
      const match = members.find((member) => member.user_id === userId);
      return match === undefined ? undefined : orgMemberName(match);
    };
  }, [roster.data, roster.isError, roster.isSuccess]);
  const definitionCard = (
    definition: OrgSettingDefinition,
    section: 'module' | 'details' | 'organization',
  ) => (
    <OrgSettingDefinitionCard
      key={definition.key}
      orgRef={orgRef}
      definition={definition}
      stored={served.data?.values[definition.key]}
      state={served.data?.state[definition.key]}
      refreshing={served.isFetching}
      personName={personName}
      testId={`${PREFIX}-${section}-def-${definition.key}`}
      // An older backend serves no verdict: its `requires` string decides, as it always has.
      canChange={
        definition.can_change ??
        (settled !== null && holdsRequirement(settled, definition.requires))
      }
      sameAs={sameAs}
      // A kept address is edited in a form, so its dirty state reaches the page's guard (seam B's law).
      registerDirty={
        definition.kind === 'address' ? registerDirtyFor(`fact:${definition.key}`) : undefined
      }
    />
  );

  return (
    <div className="flex flex-col gap-32" {...testIdProp(PREFIX)}>
      {module !== null && settled !== null && showsModule && (
        <OrgSettingsSection
          id="module"
          title={`${module.label} settings`}
          testId={`${PREFIX}-module`}
          hidden={!moduleDraws}
        >
          {definitions.map((definition) => definitionCard(definition, 'module'))}
          {panels.map((panel) => {
            const Panel = panel.component;
            const registerDirty = registerDirtyFor(panel.id);
            return (
              <PanelSlot
                key={panel.id}
                testId={`${PREFIX}-module-${panel.id}`}
                registerDirty={registerDirty}
                onFilled={reportFilledFor(panel.id)}
              >
                <Panel
                  orgRef={orgRef}
                  module={module.slug}
                  permissions={settled}
                  standing={standings?.[module.slug] ?? null}
                  config={config}
                  onNavigate={onNavigate}
                  registerDirty={registerDirty}
                />
              </PanelSlot>
            );
          })}
        </OrgSettingsSection>
      )}
      <OrgSettingsSection
        id="your-settings"
        title="Your settings"
        hint={showsLeave ? YOUR_SETTINGS_HINT_WITH_LEAVE : YOUR_SETTINGS_HINT}
        testId={`${PREFIX}-your`}
      >
        {yourSettingsLead}
        <OrgAdminPreferencesPanel
          config={config}
          landingOptions={landingOptions}
          defaultResolvesTo={defaultResolvesTo}
          headingHidden
        />
        {showsLeave && onLeft !== undefined && <OrgLeaveCard config={config} onLeft={onLeft} />}
      </OrgSettingsSection>
      {settled !== null && showsDetails && (
        <OrgSettingsSection
          id="organization-details"
          title={ORGANIZATION_DETAILS_TITLE}
          testId={`${PREFIX}-details`}
        >
          {records.map((record) => (
            <OrgSettingRecordCard
              key={record.source}
              record={record}
              testId={`${PREFIX}-details-record-${record.source}`}
            />
          ))}
          {factDefinitions.map((definition) => definitionCard(definition, 'details'))}
        </OrgSettingsSection>
      )}
      {settled !== null && showsOrganization && (
        <OrgSettingsSection
          id="organization"
          title={ORGANIZATION_SETTINGS_TITLE}
          testId={`${PREFIX}-organization`}
        >
          {orgDefinitions.map((definition) => definitionCard(definition, 'organization'))}
          {heldCards.map((card) => {
            const HostCard = card.component;
            // An empty card slot takes no space either (a dark card renders nothing; see `PanelSlot`).
            return (
              <div
                key={card.id}
                className="empty:hidden"
                {...testIdProp(`${PREFIX}-organization-${card.id}`)}
              >
                <HostCard
                  orgRef={orgRef}
                  config={config}
                  permissions={settled}
                  capabilities={capabilities}
                  onNavigate={onNavigate}
                />
              </div>
            );
          })}
          {drawsDeactivate && (
            <OrgDeactivationCard
              config={config}
              onDeactivated={onDeactivated}
              clearsEnteredAddress={clearsEnteredAddress}
            />
          )}
        </OrgSettingsSection>
      )}
    </div>
  );
}
